import React from 'react';

/**
 * LinkItemDialog — shown when a manually added item looks like it may already
 * exist in the catalog (cross-reference candidates at or above the suggest
 * threshold from utils/itemCrossReference). The user either links the new
 * line onto an existing item (it becomes another way to buy that item — no
 * duplicate is created), keeps it as a separate item, or skips the row.
 *
 * Purely presentational: the parent enriches candidates (category/unit plus
 * current vendors and packs via purchaseOptionService.fetchOptionsForItems)
 * and owns the sequential queue when several rows from one batch need review.
 *
 * Props:
 *   candidates        up to 3 enriched matches:
 *                     { itemId, itemDescription, score, reason, decision,
 *                       category, unit, packSize, vendors: [], packs: [] }
 *                     AI-suggested candidates (merged in from
 *                     services/aiItemMatchService) carry aiMatch: true and a
 *                     free-text reason — they render with an "AI match" badge
 *                     instead of a matcher reason-code label.
 *   newItemDraft      the manual row being added: { name, vendor, unit, packSize }
 *   queuePosition     optional { index, total } — "Item 2 of 3" when several
 *                     rows from the same batch are queued for review
 *   onLink(candidate) link the draft onto this existing item
 *   onCreateSeparate() insert the draft as its own new item
 *   onCancel()        abort this row (no insert, no link)
 */

// Human-readable labels for the matcher's reason codes (see
// utils/itemCrossReference.js); unknown codes fall through as-is.
const REASON_LABELS = {
  exact_name: 'Same item name',
  name_containment: 'Name contained in this item',
  name_similarity: 'Similar item name',
  strict_specification: 'Same name on a strict-spec item',
  ambiguous: 'Best of several similar items',
};

const reasonLabel = (reason) => REASON_LABELS[reason] || reason || 'Possible match';

const aiBadgeStyle = {
  display: 'inline-block', padding: '1px 6px', borderRadius: '999px',
  backgroundColor: '#EDE7F6', color: '#5E35B1', fontSize: '0.62rem', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: '4px',
};

const cancelButtonStyle = {
  fontSize: '0.84rem', fontWeight: 600, padding: '7px 16px', borderRadius: '8px',
  border: '1px solid #D4C4B0', backgroundColor: '#fff', color: '#5A4A42', cursor: 'pointer',
};

const separateButtonStyle = {
  fontSize: '0.84rem', fontWeight: 700, padding: '7px 16px', borderRadius: '8px',
  border: '1px solid #1976D2', backgroundColor: '#fff', color: '#1976D2', cursor: 'pointer',
};

export default function LinkItemDialog({
  candidates = [],
  newItemDraft = {},
  queuePosition = null,
  onLink,
  onCreateSeparate,
  onCancel,
}) {
  const draftName = (newItemDraft?.name || '').trim() || 'This item';
  const draftMeta = [newItemDraft?.vendor, newItemDraft?.packSize, newItemDraft?.unit]
    .map((value) => (value || '').toString().trim())
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 1500,
        backgroundColor: 'rgba(10,15,25,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'calc(env(safe-area-inset-top, 0px) + 12px) 12px calc(env(safe-area-inset-bottom, 0px) + 12px)',
        boxSizing: 'border-box',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${draftName} looks like it may already exist`}
        style={{
          backgroundColor: '#FDFBF7', borderRadius: '14px', border: '1px solid #D9CEC2',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', width: '100%', maxWidth: '520px',
          maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px 8px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#3E2723', lineHeight: 1.35 }}>
              {draftName} looks like it may already exist
            </div>
            {queuePosition && queuePosition.total > 1 && (
              <div style={{ fontSize: '0.72rem', color: '#A89890', whiteSpace: 'nowrap', paddingTop: '2px', flexShrink: 0 }}>
                Item {queuePosition.index} of {queuePosition.total}
              </div>
            )}
          </div>
          <div style={{ fontSize: '0.82rem', color: '#5A4A42', lineHeight: 1.55, marginTop: '6px' }}>
            Link it as another way to buy an existing item, or keep it as a separate item.
            {draftMeta && <span style={{ color: '#8B7B73' }}>{' '}You entered: {draftMeta}.</span>}
          </div>
        </div>

        {/* Candidate cards */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '2px 20px' }}>
          {candidates.map((candidate) => {
            const meta = [candidate.category, candidate.unit, candidate.packSize]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={candidate.itemId}
                data-testid="link-candidate"
                style={{
                  border: '1px solid #EDE6DE', borderRadius: '10px', backgroundColor: '#fff',
                  padding: '10px 12px', margin: '8px 0',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#3E2723' }}>
                    {candidate.itemDescription}
                  </div>
                  {meta && (
                    <div style={{ fontSize: '0.72rem', color: '#A89890', marginTop: '1px' }}>
                      {meta}
                    </div>
                  )}
                  {candidate.vendors?.length > 0 && (
                    <div style={{ fontSize: '0.72rem', color: '#5A4A42', marginTop: '2px' }}>
                      Currently bought from: {candidate.vendors.join(', ')}
                    </div>
                  )}
                  {candidate.aiMatch ? (
                    <div style={{ fontSize: '0.7rem', color: '#8B7B73', marginTop: '3px' }} data-testid="link-candidate-ai">
                      <span style={aiBadgeStyle}>AI match</span>
                      {candidate.reason}
                      {Number.isFinite(candidate.score) ? ` · ${Math.round(candidate.score * 100)}% confidence` : ''}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.7rem', color: '#8B7B73', marginTop: '3px' }}>
                      {reasonLabel(candidate.reason)}
                      {Number.isFinite(candidate.score) ? ` · ${Math.round(candidate.score * 100)}% match` : ''}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onLink?.(candidate)}
                  style={{
                    fontSize: '0.8rem', fontWeight: 700, padding: '7px 12px', borderRadius: '8px',
                    border: 'none', backgroundColor: '#1976D2', color: '#fff', cursor: 'pointer',
                    flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                >
                  Link to this item
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px 16px', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button type="button" onClick={onCancel} style={cancelButtonStyle}>
            Skip this item
          </button>
          <button type="button" onClick={onCreateSeparate} style={separateButtonStyle}>
            Keep as separate item
          </button>
        </div>
      </div>
    </div>
  );
}
